/**
 * Your hand: arrange it by picking a card and nudging it along with the arrows.
 *
 * There is no dragging. It was hard to do on a phone and easy to set off by accident
 * while tapping a card to play it. Ben's call, 16 September 2026. scope.md §8.4.
 */

import type { Card } from '@garbage/shared';
import { plural } from '@garbage/shared';
import { nudge } from '../handOrder.js';
import { CardFace, type CardSize } from './Card.js';
import type { PackId } from '../packs.js';
import { useT } from '../i18n.js';

/**
 * A hand runs from 7 to 14 cards. Every one of them has to be on screen — a card you
 * have to scroll to find is a card you forget you are holding — so the strip wraps
 * and the cards shrink as the hand grows.
 */
export function cardSizeFor(count: number): CardSize {
  // Seven 48px cards plus gaps just fit a 375px phone; eight do not.
  if (count >= 8) return 'small';
  return 'medium';
}

export function Hand({
  cards, pack, onReorder, onSelect, selectedId, canAct,
}: {
  cards: Card[];
  pack: PackId;
  onReorder: (order: string[]) => void;
  onSelect: (card: Card) => void;
  selectedId: string | null;
  /** Whether tapping a card also means something in the game right now. */
  canAct: boolean;
}) {
  void canAct;
  const t = useT();
  const ids = cards.map((c) => c.id);
  const selectedIndex = selectedId === null ? -1 : ids.indexOf(selectedId);

  return (
    <section className="px-1.5 pb-[calc(0.5rem+var(--pad-bottom))]" data-testid="hand">
      <div className="mb-1 flex min-h-8 items-center gap-2 px-1.5">
        <span className="text-[10px] font-bold uppercase tracking-wide text-bone/50">
          {plural(t, 'hand.count', cards.length)}
        </span>

        {/* Only worth showing once a card is picked up. */}
        {selectedIndex >= 0 && (
          <span className="ml-auto flex items-center gap-1.5" data-testid="nudge-controls">
            <span className="text-[10px] text-bone/50">{t('hand.move')}</span>
            <button
              type="button"
              data-testid="nudge-left"
              aria-label={t('hand.moveLeft')}
              disabled={selectedIndex === 0}
              onClick={() => onReorder(nudge(ids, selectedId!, -1))}
              className="h-8 min-h-8 w-9 min-w-9 rounded-lg bg-white/10 text-base font-black
                         disabled:opacity-25 active:bg-sun active:text-ink"
            >
              ◀
            </button>
            <button
              type="button"
              data-testid="nudge-right"
              aria-label={t('hand.moveRight')}
              disabled={selectedIndex === cards.length - 1}
              onClick={() => onReorder(nudge(ids, selectedId!, 1))}
              className="h-8 min-h-8 w-9 min-w-9 rounded-lg bg-white/10 text-base font-black
                         disabled:opacity-25 active:bg-sun active:text-ink"
            >
              ▶
            </button>
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-end justify-center gap-0.5 pb-1">
        {cards.length === 0 && <p className="py-4 text-sm text-bone/50">{t('hand.empty')}</p>}
        {cards.map((card) => (
          <button
            key={card.id}
            type="button"
            data-hand-card={card.id}
            data-testid={`hand-${card.id}`}
            aria-label={card.rank}
            aria-pressed={selectedId === card.id}
            onClick={() => onSelect(card)}
            className="shrink-0 rounded-lg"
          >
            <CardFace
              card={card}
              size={cardSizeFor(cards.length)}
              selected={selectedId === card.id}
              pack={pack}
            />
          </button>
        ))}
      </div>
    </section>
  );
}
